'use client';
import { SessionContext } from '@benzinga/session-context';
import styled from '@benzinga/themetron';
import { noop } from '@benzinga/utils';
import { Watchlist, WatchlistManager } from '@benzinga/watchlist-manager';
import React from 'react';
import { dropdownDescriptionHelper } from '../SearchBar';
import { AutocompleteModule, ActionModule, TagModule, ClipboardModule, QueryScore, SearchModule } from './Modules';
import { SearchContext, SearchContextType } from '../context';
import {
  SearchItem,
  WatchlistModuleId,
  WatchlistSearchItem,
  WatchlistSearchItemData,
  WatchlistSearchItemVersion,
  createWatchlistSearchItem,
  isLegacyWatchlistSearchItem,
  isWatchlistSearchItem,
} from '@benzinga/search-modules';
import { Watchlist as WathclistIcon } from '@benzinga/themed-icons';
import { WidgetContext, WidgetId } from '@benzinga/widget-tools';

export interface WatchlistSearchModule extends SearchModule<WatchlistSearchItemData> {
  id: typeof WatchlistModuleId;
  autocomplete: AutocompleteModule<WatchlistSearchItemData>;
  tag: TagModule;
  action: ActionModule;
  clipboard: ClipboardModule;
}

export const createWatchlistAutocompleteModule = (
  watchlistManager: WatchlistManager,
  searchContext: SearchContextType,
  widgetId?: WidgetId,
): AutocompleteModule<WatchlistSearchItemData> => {
  return {
    dropdown: (item: SearchItem) => {
      if (!isWatchlistSearchItem(item)) return undefined;
      const wlName = watchlistManager.getStoredWatchlistById(item.data.watchlistId)?.name;
      return {
        component: dropdownDescriptionHelper('Watchlist', wlName ?? 'WATCHLIST NOT FOUND'),
        id: item.id,
        locked: false,
        searchItem: item,
        type: 'AutocompleteDropdown',
        version: WatchlistSearchItemVersion,
      };
    },
    onSelect: (item: SearchItem) => {
      if (!isWatchlistSearchItem(item)) return undefined;
      return item;
    },
    query: (text: string, limit: number, selectedItems?: SearchItem[]) => {
      const lowercaseText = text.toLowerCase();
      const allWatchlists = watchlistManager.getStoredWatchlists();
      const filteredWatchlists = allWatchlists.filter(
        wl =>
          selectedItems == null ||
          !selectedItems.some(item => isWatchlistSearchItem(item) && item.data.watchlistId === wl.watchlistId),
      );
      const scoredWatchlist = filteredWatchlists.flatMap<[Watchlist, QueryScore]>(watchlist => {
        if (lowercaseText === '') {
          if (
            searchContext
              .getRecentModuleItem(widgetId)
              .some(item => isWatchlistSearchItem(item) && item.data.watchlistId === watchlist.watchlistId)
          )
            return [[watchlist, QueryScore.HistoryMatch]];
          return [[watchlist, QueryScore.NoMatch]];
        }
        if (watchlist.name.toLowerCase() === lowercaseText) {
          return [[watchlist, QueryScore.FullMatch]];
        }
        if (watchlist.name.toLowerCase().startsWith(lowercaseText)) {
          return [[watchlist, QueryScore.StartsWithMatch]];
        }
        if (watchlist.name.toLowerCase().includes(lowercaseText)) {
          return [[watchlist, QueryScore.IncludesMatch]];
        }
        return [];
      });
      const sortedWatchlists = scoredWatchlist.sort(
        (scoredWatchlist1, scoredWatchlist2) => scoredWatchlist2[1] - scoredWatchlist1[1],
      );
      const result = sortedWatchlists
        .slice(undefined, limit)
        .map<
          [SearchItem<WatchlistSearchItemData>, QueryScore]
        >(scoredWatchlist => [createWatchlistSearchItem(scoredWatchlist[0].watchlistId), scoredWatchlist[1]]);
      return {
        cancel: noop,
        promise: new Promise(resolve => {
          resolve({ canceled: false, ok: result });
        }),
      };
    },
  };
};

export const createWatchlistTagModule = (watchlistManager: WatchlistManager): TagModule => {
  return {
    render: item => {
      if (!isWatchlistSearchItem(item)) return undefined;
      const name = watchlistManager.getStoredWatchlistById(item.data.watchlistId)?.name;
      return {
        icon: <StyledWatchlistIcon />,
        text: name ?? 'WATCHLIST NOT FOUND',
        type: 'text',
      };
    },
    tagInputText: item => {
      if (!isWatchlistSearchItem(item)) return undefined;
      const name = watchlistManager.getStoredWatchlistById(item.data.watchlistId)?.name;
      return name ?? '';
    },
  };
};

export const createWatchlistActionModule = (): ActionModule => {
  return {
    dropdown: () => {
      return [];
    },
  };
};

export const createWatchlistClipboardModule = (): ClipboardModule => {
  return {
    onCopyJSON: token => {
      if (!isWatchlistSearchItem(token)) return undefined;
      return [token];
    },
    onPasteJSON: (token): WatchlistSearchItem | undefined => {
      if (isLegacyWatchlistSearchItem(token)) return WatchlistModuleItemLoader(token);
      return undefined;
    },
  };
};

const WatchlistModuleItemLoader = (item: SearchItem) => {
  if (!isLegacyWatchlistSearchItem(item)) return undefined;

  switch (item.version) {
    case WatchlistSearchItemVersion:
    default:
      return item;
  }
};

export const createWatchlistSearchModule = (
  watchlistManager: WatchlistManager,
  searchContext: SearchContextType,
  widgetId?: WidgetId,
): WatchlistSearchModule => {
  return {
    action: createWatchlistActionModule(),
    autocomplete: createWatchlistAutocompleteModule(watchlistManager, searchContext, widgetId),
    clipboard: createWatchlistClipboardModule(),
    id: WatchlistModuleId,
    loader: WatchlistModuleItemLoader,
    tag: createWatchlistTagModule(watchlistManager),
  };
};

export const useWatchlistSearchModule = () => {
  const session = React.useContext(SessionContext);
  const search = React.useContext(SearchContext);
  const watchlistManager = session.getManager(WatchlistManager);
  const widgetContext = React.useContext(WidgetContext);
  return React.useMemo(
    () => createWatchlistSearchModule(watchlistManager, search, widgetContext.widgetId),
    [watchlistManager, search, widgetContext.widgetId],
  );
};

const StyledWatchlistIcon = styled(WathclistIcon)`
  vertical-align: middle;
  width: 1em;
  height: 1em;
  margin-right: 4px;
`;
